import type { ClRow, CommLogData } from './types';

const ROWS: ClRow[] = [
  {
    id: 'r1', patient: 'Marcia Hollis', agent: 'reminders', purpose: 'Confirm Tue 2:30 with Dr. Lin',
    channel: 'text', verdict: 'sent', outcome: 'Replied', outcomeTone: 'good', time: '2 min ago',
    absoluteTime: 'Today · 9:58am', day: 'today', rank: '#1 of 3 for this patient today',
    body: 'Hi Marcia — confirming Tuesday 2:30pm with Dr. Lin. Reply YES to confirm or call us to change it.',
  },
  {
    // Sent in Farsi, and the row says so (amendment 25 §5). A practice that has
    // switched a language on needs to see that a message really went in it.
    id: 'r1-fa', patient: 'Maryam Rezaei', agent: 'reminders',
    purpose: 'Reminder — cleaning tomorrow 11:15',
    channel: 'text', language: 'fa', verdict: 'sent', outcome: 'Confirmed by text',
    outcomeTone: 'good', time: '6 min ago',
    absoluteTime: 'Today · 9:54am', day: 'today',
    body: 'سلام مریم، ممنون که نوبتتان را تأیید کردید — فردا ساعت ۱۱:۱۵ با دکتر سایتو منتظرتان هستیم.',
  },
  {
    // Confirmed from the email's button — the new event kind (§6).
    id: 'r1-confirm', patient: 'Devon Achebe', agent: 'reminders',
    purpose: 'Reminder — filling Thu 14:30',
    channel: 'email', verdict: 'sent', outcome: 'Confirmed by email',
    outcomeTone: 'good', time: '18 min ago',
    absoluteTime: 'Today · 9:42am', day: 'today',
    subject: 'Your appointment on Thursday 16 October',
    body: 'Hi Devon — this is a reminder of your appointment with Dr. Chen on Thursday 16 October at 14:30 for a filling.',
  },
  // The regression row (spec 18 §12). This was inbound all along, wearing an
  // outbound verdict — nobody "sent" it; the patient rang. It now carries a
  // direction and a disposition, and the outcome says who took the transfer
  // rather than restating the chip.
  {
    id: 'r2', direction: 'inbound', patient: 'Carlos Reyes', agent: 'receptionist',
    purpose: 'Throbbing pain since yesterday, jaw now swollen',
    channel: 'call', verdict: 'sent', disposition: 'warm_transfer',
    outcome: 'Dr. Okafor took it', outcomeTone: 'alert', time: '6 min ago',
    absoluteTime: 'Today · 9:54am', day: 'today', durationSec: 68, hasRecording: true,
    transcript: [
      { who: 'Patient', text: 'I’ve had this throbbing pain since yesterday and now my jaw is swollen.', at: '0:06' },
      { who: 'ELVA', text: 'That needs to be seen today. Let me get you to the on-call provider now.', at: '0:14', isAgent: true },
      { who: 'ELVA', text: 'Transferring you to Dr. Okafor.', at: '0:52', isAgent: true },
    ],
  },
  {
    id: 'r3', patient: 'Renata Oyelaran', agent: 'ar', purpose: 'Older balances · Balance $184 · what the plan paid + link',
    channel: 'text', verdict: 'sent', outcome: 'Paid', outcomeTone: 'good', time: '22 min ago',
    absoluteTime: 'Today · 9:38am', day: 'today', rank: '#1 of 2 for this patient today',
    body: 'Hi Renata — your plan covered $232 of the cleaning; the balance is $184. You can pay securely here: [link]. Thanks!',
  },
  {
    id: 'r4', patient: 'Grace Winslow', agent: 'treatment', purpose: 'Case follow-up — crown #14',
    channel: 'text', verdict: 'suppressed', outcome: 'Something else went instead', outcomeTone: 'muted',
    reason: '$340 balance — this practice does not mix A/R with treatment', time: '34 min ago',
    absoluteTime: 'Today · 9:26am', day: 'today',
    body: '[Drafted, not sent] Hi Grace — Dr. Saito flagged a crown on #14. Shall we get you booked?',
  },
  {
    id: 'r5', patient: 'Todd Bergman', agent: 'invisalign', purpose: 'Email 3 — what it costs, and what your plan covers',
    channel: 'email', verdict: 'blocked', outcome: 'Rewritten and sent', outcomeTone: 'muted',
    reason: 'ELVA’s check held the estimate: the figure didn’t match the plan. Rewritten and sent', time: '41 min ago',
    absoluteTime: 'Today · 9:19am', day: 'today', rank: '#2 of 4 this week',
    subject: 'What it costs, and what your plan covers',
    body: 'Most plans cover a share of clear aligners, and many patients have orthodontic benefit they’ve never used. Here’s what to expect — [plan page].',
  },
  {
    id: 'r6', patient: 'Daniel Ostrowski', agent: 'treatment', purpose: 'Consult offer — crown',
    channel: 'text', verdict: 'held', outcome: 'Gap Filler has it', outcomeTone: 'muted',
    reason: 'Gap Filler is offering him Thursday 2:00pm for the same crown', time: '48 min ago',
    absoluteTime: 'Today · 9:12am', day: 'today',
    body: '[Drafted, not sent] Hi Daniel — ready to schedule that crown? We can find a time that works.',
  },
  {
    id: 'r7', patient: 'Sandra Beaulieu', agent: 'ar', onBehalfOf: 'ar', purpose: 'Older balances · Bill explained — paid on the call',
    channel: 'call', verdict: 'sent', outcome: 'Paid', outcomeTone: 'good', time: '1h ago',
    absoluteTime: 'Today · 9:00am', day: 'today', durationSec: 134, hasRecording: true,
    transcript: [
      { who: 'ELVA', text: 'Hi Sandra, this is Elva calling from Alamo Heights Dental about your balance.', at: '0:02', isAgent: true },
      { who: 'Patient', text: 'Oh yes — how much is it and what did insurance cover?', at: '0:11' },
      { who: 'ELVA', text: 'Your plan paid $410; the remaining balance is $96. I can take that now if you like.', at: '0:18', isAgent: true },
      { who: 'Patient', text: 'Sure, let’s do it.', at: '0:31' },
    ],
  },
  {
    id: 'r8', patient: 'Linda Ramirez', agent: 'recall', onBehalfOf: 'recall', purpose: 'Hygiene due — reminder call',
    channel: 'call', verdict: 'sent', outcome: 'Voicemail', outcomeTone: 'muted', time: '1h ago',
    absoluteTime: 'Today · 8:52am', day: 'today', durationSec: 22, hasRecording: true,
    transcript: [
      { who: 'ELVA', text: 'Hi Linda, it’s time for your hygiene visit — we’d love to get you back on the schedule.', at: '0:02', isAgent: true },
      { who: 'ELVA', text: 'Give us a call back when you have a moment. Thanks!', at: '0:14', isAgent: true },
    ],
  },
  {
    id: 'r9', patient: 'Maria Gonzalez', agent: 'recall', purpose: 'Recall email — hygiene',
    channel: 'email', verdict: 'suppressed', outcome: 'Permanent', outcomeTone: 'muted',
    reason: 'Opted out of recall emails — reminders and post-op unaffected', time: '1h ago',
    absoluteTime: 'Today · 8:41am', day: 'today',
    subject: 'Time for your hygiene visit',
    body: '[Drafted, not sent] Hi Maria — you’re due for a cleaning. Want me to find you a time?',
  },
  {
    id: 'r10', patient: '14 reactivation patients', batchCount: 14, agent: 'recall', purpose: 'Reactivation email batch',
    channel: 'email', verdict: 'held', outcome: 'Retries in 3 days', outcomeTone: 'muted',
    reason: 'At their contact limit this fortnight', time: '2h ago',
    absoluteTime: 'Today · 7:58am', day: 'today',
    body: '[Drafted, not sent] It’s been a while — we’d love to see you back at Alamo Heights Dental. [book]',
    batchNames: ['Owen Hart', 'Priscilla Vaughn', 'Dominic Ashby', 'Rosa Iglesias', 'Nate Fujimoto', 'Bianca Sol', 'Kofi Mensah', 'Talia Brenner', 'Ivan Petrov', 'Della Okonjo', 'Sam Whitlock', 'Nadia Rahimi', 'Lars Eklund', 'Yara Haddad'],
  },
  {
    id: 'r11', patient: 'James Harrison', agent: 'reminders', purpose: 'Confirm perio maintenance · 18 Oct',
    channel: 'text', verdict: 'sent', outcome: 'Booked', outcomeTone: 'good', time: '2h ago',
    absoluteTime: 'Today · 7:40am', day: 'today', rank: '#1 of 1 for this patient',
    body: 'Hi James — you’re due for perio maintenance. I have Fri 18 Oct at 11:00 — want it? Reply YES.',
  },
  {
    id: 'r12', patient: 'Kevin Wright', agent: 'reminders', purpose: 'Confirm cleaning · 3 Apr',
    channel: 'text', verdict: 'held', outcome: 'Queued for 9:00am', outcomeTone: 'muted',
    reason: 'Quiet hours — the reply is held until 9:00am', time: 'Yesterday',
    absoluteTime: 'Yesterday · 8:41pm', day: 'yesterday',
    body: '[Drafted, not sent] Hi Kevin — just confirming your cleaning on 3 Apr. Reply YES or tap to change.',
  },
  {
    id: 'r13', patient: 'Priya Raman', agent: 'treatment', purpose: 'Plan link — onlay #3',
    channel: 'email', verdict: 'sent', outcome: 'Clicked', outcomeTone: 'good', time: 'Yesterday',
    absoluteTime: 'Yesterday · 6:12pm', day: 'yesterday', rank: '#1 for this patient',
    subject: 'Your treatment plan — onlay #3',
    body: 'Here’s the plan Dr. Chen put together, with the estimate and what your plan covers: [plan page].',
  },
  {
    id: 'r14', patient: 'Theresa Kimball', agent: 'ar', purpose: 'New balances · Balance reminder',
    channel: 'text', verdict: 'skipped', outcome: 'Dropped — no longer needed', outcomeTone: 'muted',
    reason: 'Balance re-evaluated after a posted insurance payment — now $18, under the $25 floor', time: 'Yesterday',
    absoluteTime: 'Yesterday · 5:31pm', day: 'yesterday',
    body: '[Drafted, not sent] Hi Theresa — a small balance of $18 remains on your account. [pay]',
  },
  {
    id: 'r15', patient: 'Aaron Vasquez', agent: 'ar', purpose: 'Over 90 days · Balance over $1,000',
    channel: 'text', verdict: 'blocked', outcome: 'Never — not permitted', outcomeTone: 'muted',
    reason: 'Over $1,000: a person calls, ELVA doesn’t message', time: 'Yesterday',
    absoluteTime: 'Yesterday · 4:20pm', day: 'yesterday',
    body: '[Not permitted] Over $1,000 is handled by a person, from Triage, with the cheat sheet.',
  },
  {
    id: 'r16', patient: 'Owen Delacroix', agent: 'gap-filler', purpose: 'Gap offer — Thu 2:00pm, Op 3',
    channel: 'email', verdict: 'failed', outcome: 'Bounced — fix the address', outcomeTone: 'alert',
    reason: 'The email bounced — the address on file is no longer valid', time: 'Yesterday',
    absoluteTime: 'Yesterday · 2:04pm', day: 'yesterday',
    subject: 'A last-minute opening on Thursday',
    body: 'A spot just opened Thursday at 2:00pm — first come, first served. Want it? [book]',
  },
  {
    id: 'r17', patient: 'Nadia Rahimi', agent: 'reminders', onBehalfOf: 'reminders', purpose: 'Reminder call — cleaning tomorrow',
    channel: 'call', verdict: 'sent', outcome: 'No reply yet', outcomeTone: 'muted', time: 'Yesterday',
    absoluteTime: 'Yesterday · 11:10am', day: 'yesterday', durationSec: 0, hasRecording: false,
    transcript: [{ who: 'ELVA', text: 'Reminder that you have a cleaning tomorrow at 10:30. See you then!', at: '0:00', isAgent: true }],
  },
  {
    id: 'r18', patient: 'Delia Mbeki', agent: 'ar', purpose: 'Older balances · Step 4 — plan reminder',
    channel: 'text', verdict: 'sent', outcome: 'Delivered', outcomeTone: 'muted',
    reason: 'sent without the plan sentence — this practice has payment plans switched off', time: 'Yesterday',
    absoluteTime: 'Yesterday · 9:15am', day: 'yesterday', shared: true,
    body: 'Hi Delia — a friendly reminder about your balance. You can pay here anytime: [link].',
  },

  /* ---- Inbound (spec 18). One row per contact, and these are contacts the
   * practice did not initiate — so they carry a disposition, never a verdict.
   * All seven dispositions appear at least once, because each renders
   * differently and a vocabulary nobody can see is a vocabulary nobody checks. */

  {
    id: 'i1', direction: 'inbound', patient: 'Priya Raman', agent: 'receptionist',
    purpose: 'Wants a cleaning, asked for anything after 4pm',
    channel: 'call', verdict: 'sent', disposition: 'booked',
    appointmentReason: 'Hygiene — recall cleaning',
    outcome: 'Tue 4:30 · Hygiene with Dr. Lin', outcomeTone: 'good', time: '14 min ago',
    absoluteTime: 'Today · 9:46am', day: 'today', durationSec: 142, hasRecording: true,
    transcript: [
      { who: 'Patient', text: 'I think I am overdue for a clean — do you have anything after work?', at: '0:05' },
      { who: 'ELVA', text: 'I can do Tuesday at 4:30 with Dr. Lin. Does that work?', at: '0:21', isAgent: true },
    ],
  },
  {
    // An unmatched caller is a REAL ROW, not a blank (§4.1). Often a new patient,
    // which is the most valuable kind — so it renders the number and says plainly
    // that the chart has nobody behind it.
    id: 'i2', direction: 'inbound', patient: '', callerNumber: '(210) 555‑0139', agent: 'receptionist',
    purpose: 'Asked what a new patient exam costs and whether they take her plan',
    channel: 'call', verdict: 'sent', disposition: 'booked',
    appointmentReason: 'New patient exam',
    outcome: 'Thu 10:00 · New patient exam', outcomeTone: 'good', time: '31 min ago',
    absoluteTime: 'Today · 9:29am', day: 'today', durationSec: 205, hasRecording: true,
    transcript: [
      { who: 'Patient', text: 'I have not been in before — do you take Delta Dental?', at: '0:03' },
      { who: 'ELVA', text: 'We do. A new patient exam and x-rays is $189 before benefits.', at: '0:12', isAgent: true },
    ],
  },
  {
    id: 'i3', direction: 'inbound', patient: 'Howard Feltz', agent: 'receptionist',
    purpose: 'Cannot make Thursday, wants the same week if possible',
    channel: 'text', verdict: 'sent', disposition: 'moved',
    appointmentReason: 'Crown — prep or seat',
    outcome: 'Was Thu 9:00 → now Tue 2:30', outcomeTone: 'muted', time: '52 min ago',
    absoluteTime: 'Today · 9:08am', day: 'today',
    body: 'Sorry, something came up Thursday — can I come earlier in the week instead?',
  },
  {
    id: 'i4', direction: 'inbound', patient: 'Sonia Vargas', agent: 'receptionist',
    purpose: 'Asked whether the office opens on Saturday',
    channel: 'text', verdict: 'sent', disposition: 'answered',
    outcome: 'Nothing further', outcomeTone: 'muted', time: '1 hr ago',
    absoluteTime: 'Today · 8:57am', day: 'today',
    body: 'Are you open this Saturday?',
  },
  {
    id: 'i5', direction: 'inbound', patient: 'Gabriel Osei', agent: 'receptionist',
    purpose: 'Wants Dr. Saito to ring him about the crown quote',
    channel: 'call', verdict: 'sent', disposition: 'message',
    outcome: 'For Dr. Saito · not yet read', outcomeTone: 'muted', time: '1 hr ago',
    absoluteTime: 'Today · 8:44am', day: 'today', durationSec: 61, hasRecording: true,
    reason: 'He wants to talk through the quote before he books — asked for a call back after 5pm.',
    transcript: [
      { who: 'Patient', text: 'Could Dr. Saito give me a ring about the crown? After five is best.', at: '0:08' },
    ],
  },
  {
    // The one outcome that is time-sensitive rather than historical, and the same
    // fact the Dashboard's "3 still waiting" tile counts.
    id: 'i6', direction: 'inbound', patient: 'Marguerite Boyd', agent: 'receptionist',
    purpose: 'Swelling under a crown placed last week, cannot close her bite',
    channel: 'call', verdict: 'sent', disposition: 'urgent_triage',
    outcome: 'Waiting', outcomeTone: 'alert', time: '1 hr ago',
    absoluteTime: 'Today · 8:31am', day: 'today', durationSec: 96, hasRecording: true,
    reason: 'Swelling plus an inability to close the bite after a recent crown — triaged rather than booked.',
    transcript: [
      { who: 'Patient', text: 'The side of my face is swollen and my teeth do not meet properly.', at: '0:11' },
      { who: 'ELVA', text: 'I am putting you through to someone now — please do not wait on this.', at: '0:24', isAgent: true },
    ],
  },
  {
    id: 'i7', direction: 'inbound', patient: 'Ellis Kwan', agent: 'receptionist',
    purpose: 'Cancelling Friday — going away for work',
    channel: 'call', verdict: 'sent', disposition: 'cancelled',
    outcome: 'Slot released · Gap Filler is offering it', outcomeTone: 'muted', time: '2 hr ago',
    absoluteTime: 'Today · 8:02am', day: 'today', durationSec: 74, hasRecording: true,
    transcript: [
      { who: 'Patient', text: 'I need to cancel Friday, I am away for work that week.', at: '0:06' },
      { who: 'ELVA', text: 'That is done. Shall I look at the week after instead?', at: '0:19', isAgent: true },
    ],
  },
  {
    id: 'i8', direction: 'inbound', patient: '', callerNumber: '(830) 555‑0271', agent: 'receptionist',
    purpose: 'Wrong number — was looking for a doctor’s surgery',
    channel: 'call', verdict: 'sent', disposition: 'answered',
    outcome: 'Nothing further', outcomeTone: 'muted', time: 'Yesterday',
    absoluteTime: 'Yesterday · 4:12pm', day: 'yesterday', durationSec: 22, hasRecording: true,
    transcript: [{ who: 'Patient', text: 'Is that the medical centre on Broadway?', at: '0:02' }],
  },
  {
    id: 'i9', direction: 'inbound', patient: 'Lucille Abara', agent: 'receptionist',
    purpose: 'Asked to speak to somebody about a bill she does not recognise',
    channel: 'call', verdict: 'sent', disposition: 'warm_transfer',
    outcome: 'Rang out', outcomeTone: 'alert', time: 'Yesterday',
    absoluteTime: 'Yesterday · 3:20pm', day: 'yesterday', durationSec: 118, hasRecording: true,
    reason: 'A billing question ELVA will not answer — money questions go to a person.',
    transcript: [
      { who: 'Patient', text: 'There is a charge on here for $212 and I have no idea what it is for.', at: '0:14' },
    ],
  },
];

const CHOSE_NOT_TO_SEND: readonly string[] = ['held', 'blocked', 'suppressed', 'skipped'];

export const COMM_LOG: CommLogData = {
  titleLead: 'Communication',
  titleAccent: 'log',
  // Spec 18 §1. The page keeps its name — Communication log is MORE accurate once
  // it holds both directions, not less.
  subtitle:
    'What came in, what went out, and what an agent decided not to send — with the reason.',
  dayTotal: 1284,
  dayChoseNotToSend: 47,
  rows: ROWS,
};

export const CL_CHOSE_NOT_TO_SEND = CHOSE_NOT_TO_SEND;
